import { useRef, useState } from "react";
import { Menu, MenuItem, Button } from "@mui/material";
import jsPDF from "jspdf";
import * as htmlToImage from "html-to-image";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import zoomPlugin from "chartjs-plugin-zoom";
import { secondsToHHMMSS } from "../../services/utilities";
import { animate, animations } from "framer-motion";
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  zoomPlugin,
);
const BarGraph = (props) => {
  const chartRef = useRef();
  const zoomRef = useRef({ min: 0, max: null });
  const containerRef = useRef(null);

  const resetZoom = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      // Clear manual overrides to allow auto-scaling (Show All)
      delete chart.options.scales.x.min;
      delete chart.options.scales.x.max;

      zoomRef.current = { min: 0, max: null };

      chart.update();

      // Manually reset scrollbar
      if (scrollContainerRef.current && scrollSpacerRef.current) {
        scrollContainerRef.current.scrollLeft = 0;
        scrollSpacerRef.current.style.width = "100%";
      }
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const downloadImage = async (format) => {
    if (!chartRef.current) return;
    const chart = chartRef.current;

    // Enable Total Count for download
    if (chart.options.plugins?.totalCountPlugin) {
      chart.options.plugins.totalCountPlugin.display = true;
      chart.update();
    }

    // Helper to trigger download
    const saveAs = (uri, filename) => {
      const link = document.createElement("a");
      if (typeof link.download === "string") {
        link.href = uri;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.open(uri);
      }
    };

    try {
      if (format === "png") {
        const url = chart.toBase64Image("image/png", 1.0);
        saveAs(url, "chart.png");
      } else if (format === "jpeg") {
        const url = chart.toBase64Image("image/jpeg", 1.0);
        saveAs(url, "chart.jpeg");
      } else if (format === "svg") {
        // html-to-image is needed for SVG of the canvas container
        const dataUrl = await htmlToImage.toSvg(chart.canvas);
        saveAs(dataUrl, "chart.svg");
      } else if (format === "pdf") {
        const canvas = chart.canvas;
        // Use higher quality image for PDF
        const imgData = canvas.toDataURL("image/png", 1.0);
        // Setup PDF
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? "l" : "p",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save("chart.pdf");
      }
    } catch (err) {
      console.error("Download failed", err);
    }

    // Restore Total Count visibility (Hidden)
    if (chart.options.plugins?.totalCountPlugin) {
      chart.options.plugins.totalCountPlugin.display = false;
      chart.update();
    }

    handleMenuClose();
  };

  const handleClick = (event) => {
    console.log("handleClick");
  };

  const handlePrint = () => {
    if (!chartRef.current) return;
    const chart = chartRef.current;

    // Enable Total Count for print
    if (chart.options.plugins?.totalCountPlugin) {
      chart.options.plugins.totalCountPlugin.display = true;
      chart.update();
    }

    const dataUrl = chart.toBase64Image("image/png", 1.0);

    // Restore Total Count visibility
    if (chart.options.plugins?.totalCountPlugin) {
      chart.options.plugins.totalCountPlugin.display = false;
      chart.update();
    }

    const printWin = window.open(
      "",
      "_blank",
      "toolbar=0,location=0,menubar=0,width=800,height=600",
    );

    if (!printWin) return;

    printWin.document.write(`
    <html>
      <head>
        <title>Print Chart</title>
        <style>
          body {
            margin: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
          }
          img {
            max-width: 100%;
            max-height: 100%;
          }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" />
      </body>
    </html>
  `);

    printWin.document.close();

    // Fires on BOTH Print and Cancel
    printWin.onafterprint = () => {
      printWin.close();
    };

    // Fallback (some browsers delay onafterprint)
    setTimeout(() => {
      if (!printWin.closed) {
        printWin.focus();
        printWin.print();
      }
    }, 300);

    handleMenuClose();
  };

  const handleFullScreen = () => {
    if (containerRef.current) {
      if (containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
      } else if (containerRef.current.webkitRequestFullscreen) {
        /* Safari */
        containerRef.current.webkitRequestFullscreen();
      } else if (containerRef.current.msRequestFullscreen) {
        /* IE11 */
        containerRef.current.msRequestFullscreen();
      }
    }
    handleMenuClose();
  };

  const scrollContainerRef = useRef(null);
  const scrollSpacerRef = useRef(null);
  // Flag to prevent recursive scroll/zoom updates
  const isUpdating = useRef(false);

  // Sync scrollbar width and position based on chart zoom/pan
  const syncScrollbar = ({ chart }) => {
    if (isUpdating.current) return;
    isUpdating.current = true;

    const { min, max } = chart.scales.x;
    zoomRef.current = { min, max };

    const dataLength = chart.data.datasets[0].data.length;

    // Calculate visible range ratio
    const visibleRange = max - min;
    const ratio = visibleRange / dataLength;

    if (scrollContainerRef.current && scrollSpacerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;

      // If fully zoomed out (or close to it), disable scroll
      if (ratio >= 0.99) {
        scrollSpacerRef.current.style.width = `${containerWidth}px`;
        scrollContainerRef.current.scrollLeft = 0;
      } else {
        // Virtual width = Container Width / Ratio
        const virtualWidth = containerWidth / ratio;
        scrollSpacerRef.current.style.width = `${virtualWidth}px`;

        // Scroll Position = (min / dataLength) * Virtual Width
        const scrollPos = (min / dataLength) * virtualWidth;
        scrollContainerRef.current.scrollLeft = scrollPos;
      }
    }
    isUpdating.current = false;
  };

  const handeScroll = (e) => {
    if (isUpdating.current || !chartRef.current) return;
    isUpdating.current = true;

    const container = e.target;
    const scrollRatio = container.scrollLeft / container.scrollWidth;
    const visibleRatio = container.clientWidth / container.scrollWidth;

    const chart = chartRef.current;
    const dataLength = chart.data.datasets[0].data.length;

    const newMin = scrollRatio * dataLength;
    const newMax = newMin + visibleRatio * dataLength;

    chart.options.scales.x.min = newMin;
    chart.options.scales.x.max = newMax;
    zoomRef.current = { min: newMin, max: newMax };

    chart.update("none");

    isUpdating.current = false;
  };

  const data = {
    labels: props.labels,
    datasets: [],
  };
  const data2 = {
    labels: props.labels,
    datasets: [],
  };
  if (props?.busiestHour) {
    props?.busiestHour?.forEach((props) => {
      data2.datasets.push({
        label: props.label ? props.label : "",
        data: props.data,
        backgroundColor: props.backgroundColor,
        hoverOffset: 4,
        borderWidth: props.border,
        barPercentage: 5,
        barThickness: 8,
        maxBarThickness: 50,
        minBarLength: 1,
        borderRadius: 1,
        // data: props.data,
      });
    });
  } else {
    data.datasets.push({
      label: props.label ? props.label : "",
      data: props.data,
      backgroundColor: props.color,
      hoverOffset: 4,
      borderWidth: props.border,
      barPercentage: 0.3,
      barThickness: props?.maxBar
        ? props.maxBar * 8
        : props.data.length > 10
          ? 10
          : 50,
      maxBarThickness: 50,
      minBarLength: 2,
      borderRadius: 2,
    });
  }
  // Plugin to draw Total Count on canvas and white background
  const totalCountPlugin = {
    id: "totalCountPlugin",
    beforeDraw: (chart) => {
      const { ctx, width, height } = chart;
      ctx.save();
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    },
    afterDraw: (chart) => {
      // Only draw if display option is true
      if (!chart.options.plugins?.totalCountPlugin?.display) {
        return;
      }
      const {
        ctx,
        chartArea: { top, right },
      } = chart;
      let total = 0;

      // Calculate total based on active dataset
      const datasets = chart.data.datasets;
      if (datasets.length > 0) {
        datasets.forEach((dataset) => {
          if (dataset.data) {
            total += dataset.data.reduce(
              (acc, curr) => acc + (Number(curr) || 0),
              0,
            );
          }
        });
      }

      ctx.save();
      ctx.font = "bold 14px Arial";
      ctx.fillStyle = "black";
      ctx.textAlign = "right";
      ctx.fillText(`Total Count: ${total}`, right, top - 10);
      ctx.restore();
    },
  };

  const options = {
    layout: {
      padding: 20,
    },
    // animations: false,

    scales: {
      x: {
        stepSize: 4,
        min: zoomRef.current.min || 0,

        max: zoomRef.current.max
          ? zoomRef.current.max
          : props.maxBar
            ? props.maxBar
            : props.data
              ? props.data.length <= 50
                ? props.data.length
                : 50
              : props.labels
                ? props.labels.length < 30
                  ? props.labels.length
                  : 30
                : null,

        grid: {
          display: false, // This removes the x-axis gridlines
        },
        ticks: {
          autoSkip: false, // Ensures all labels are shown
          beginAtZero: true,
          callback: function (value, index, values) {
            const label = this.getLabelForValue(value);
            return label.length > 8 && props.maxBar
              ? `${label.substring(0, 8)}..`
              : label;
          },
        },
      },
      y: {
        ticks: {
          callback: (value) =>
            `${props.hhmmss ? secondsToHHMMSS(value) : value} `,
        },

        title: {
          text: "sec",
          display: false,
        },
        grid: {
          display: false, // This removes the x-axis gridlines
        },
      },
    },
    responsive: true,

    maintainAspectRatio: false,
    plugins: {
      datalabels: false,
      legend: {
        position: "top",
        display: !!props.multipleBar,
      },
      zoom: {
        limits: {
          x: { min: 0, max: data.datasets[0]?.data?.length, minRange: 5 },
        },
        pan: {
          enabled: true,
          mode: "x",
          onPan: syncScrollbar,
        },
        zoom: {
          drag: {
            enabled: true,
          },
          mode: "x",
          onZoom: syncScrollbar,
        },
      },
      totalCountPlugin: {
        display: false, // Hidden by default
      },
    },
  };

  return (
    <div className={`${props.class} h-full`} ref={containerRef}>
      <Bar
        data={props.multipleBar === true ? data2 : data}
        ref={chartRef}
        onClick={handleClick}
        options={options}
        plugins={[totalCountPlugin]}
      />
      {props?.removeButtons !== true && (
        <>
          <button
            onClick={resetZoom}
            className={`mt-6 px-4  "ml-6"  py-2  bg-primary text-white rounded `}
          >
            Reset Zoom
          </button>
          <button
            onClick={handleMenuClick}
            className={`mt-6 px-4 ml-4 py-2 bg-primary text-white rounded`}
          >
            More Options
          </button>
        </>
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "download-button",
          sx: {
            "& .MuiMenuItem-root:hover": {
              backgroundColor: "#FEF4E9", // change this
            },
          },
        }}
      >
        <MenuItem onClick={handleFullScreen}>View Full Screen</MenuItem>
        <MenuItem onClick={handlePrint}>Print Chart</MenuItem>
        <div className="border-b"></div>
        <MenuItem onClick={() => downloadImage("png")}>
          Download to PNG
        </MenuItem>
        <MenuItem onClick={() => downloadImage("jpeg")}>
          Download to JPEG
        </MenuItem>
        <MenuItem onClick={() => downloadImage("svg")}>
          Download to SVG
        </MenuItem>
        <MenuItem onClick={() => downloadImage("pdf")}>
          Download to PDF
        </MenuItem>
      </Menu>
      <div
        ref={scrollContainerRef}
        onScroll={handeScroll}
        className="w-full overflow-x-auto mt-2 custom-scrollbar"
        style={{ height: "20px" }}
      >
        <div
          ref={scrollSpacerRef}
          style={{ width: "100%", height: "1px" }}
        ></div>
      </div>
    </div>
  );
};
export default BarGraph;
